import { useEffect, useState } from 'react';
import { DefaultLayout } from '../layout/DefaultLayout';
import './GlobalGameBoard.scss';
import style from './GlobalGameBoard.scss.json';
import { Gameboard } from '../components/molecules/Gameboard';
import { EventGameboard } from '../components/molecules/EventGameboard';
import { TopicScore } from 'lincd-irlcg/lib/shapes/TopicScore';
import { Topic } from 'lincd-irlcg/lib/shapes/Topic';
import { ImageObject } from 'lincd-schema/lib/shapes/ImageObject';
import Section from '../components/molecules/Section';
import MainMenu from '../components/molecules/MainMenu';
import { Container } from '../layout/Container';
import Card from '../components/molecules/Card';
import { getDesktopBreakpoints } from '../utils/desktopBreakpoint';
import { Spinner } from '../components/atoms/Spinner';
import { useTranslate } from '@tolgee/react';
import { useProfileCompleted } from '../hooks/useProfileCompleted';
import { useAuth } from 'lincd-auth/lib/hooks/useAuth';
import { Player } from 'lincd-irlcg/lib/shapes/Player';
import { UserAccount } from 'profile-plus/lib/shapes/UserAccount';
import { withRetry } from 'lincd-irlcg/lib/utils/helper';
import { Server } from 'lincd-server-utils/lib/utils/Server';
import { packageName } from '../package';
import { LocationTotals } from 'lincd-irlcg/lib/types/gameboard';

function GlobalGameBoard() {
  const { t } = useTranslate();
  const { checkIsCompleted } = useProfileCompleted();
  let prefix = 'globalGameBoard';
  const auth = useAuth<Player, UserAccount>();
  let user = auth.user;
  const isInTrialMode = user?.isInCertainMode('trial');
  let isEventTeam = user?.isEventMode();

  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );
  const [gameboardTotals, setGameboardTotals] = useState<any>();
  const [location, setLocation] = useState<LocationTotals>({
    city: 0,
    country: 0,
    region: 0,
  });
  const [loading, setLoading] = useState(true);

  // check if user has completed profile setup
  useEffect(() => {
    checkIsCompleted();
  }, []);

  const desktopBreakpoint = 1024;

  const getGameboardTotals = async () => {
    setLoading(true);
    try {
      const result = await withRetry(
        () => TopicScore.getGameBoardTotals(),
        10,
        1000 * 5,
      );
      setGameboardTotals(result);
    } catch (error) {
      console.warn(`error loading gameboard totals:`, error);
      alert(
        t(
          prefix + '.loadingError',
          'Hmm, things are a little crowded right now. We were not able to load the game board. Please try again in a moment.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const getEventTotals = async () => {
    setLoading(true);
    try {
      const result = await withRetry(
        () => TopicScore.getEventTotals(),
        10,
        1000 * 5,
      );
      setGameboardTotals(result);
    } catch (error) {
      console.warn(`error loading gameboard totals:`, error);
      alert(
        t(
          prefix + '.loadingError',
          'Hmm, things are a little crowded right now. We were not able to load the game board. Please try again in a moment.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const getLocation = async () => {
    setLoading(true);
    try {
      const response = await withRetry(
        () => Server.call(packageName, 'getTotalLocation'),
        10,
        1000 * 5,
      );
      setLocation(response);
    } catch (error) {
      console.warn(`error loading location totals:`, error);
      alert(
        t(
          prefix + '.loadingError',
          'Hmm, things are a little crowded right now. We were not able to load the location totals. Please try again in a moment.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLocation();

    if (!isEventTeam) {
      getGameboardTotals();
    } else {
      getEventTotals();
    }

    //to get breakpoint of desktop
    getDesktopBreakpoints((width) => setWidth(width));
  }, [isEventTeam]);

  // Reusable function to render Card
  const renderCard = (topic: Topic, score: number, medal: any) => {
    return (
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      <Card
        key={topic.identifier.toString()}
        identifier={'Action ' + topic.identifier}
        title={topic.name}
        description={topic.description}
        medal={medal}
        value={score}
        image={ImageObject.create({ contentUrl: topic.image.contentUrl })}
      />
    );
  };

  return (
    <DefaultLayout>
      <div className={style.bigContainer}>
        <div className={style.container}>
          {loading ? (
            <div className={style.loading}>
              <Spinner color="white" />
            </div>
          ) : (
            gameboardTotals && (
              <Container
                className={style.containerContent}
                maxWidth={width >= desktopBreakpoint ? 'md' : 'sm'}
              >
                {isEventTeam ? (
                  <EventGameboard
                    totalScore={gameboardTotals}
                    totalLocation={location}
                  />
                ) : (
                  <Gameboard
                    totalScore={gameboardTotals}
                    totalLocation={location}
                  />
                )}
                {!isEventTeam && (
                  <Section
                    title={t('word.globalPeaceActions', 'Global Peace Actions')}
                    data={gameboardTotals.globalTopicTotal.globalTopicTotal}
                    // medalion={peaceResult.globalTopicTotal.globalMedalTopic}
                    renderCardFunction={renderCard}
                  />
                )}
                {isInTrialMode || isEventTeam ? null : (
                  <>
                    <Section
                      title={t('word.teamPeaceActions', 'Team Peace Actions')}
                      data={gameboardTotals.teamTopicTotals.teamTopicTotals}
                      // medalion={peaceResult.teamTopicTotals.teamMedalTopic}
                      renderCardFunction={renderCard}
                    />
                    <Section
                      title={t('word.myPeaceActions', 'My Peace Actions')}
                      data={gameboardTotals?.myTopicTotals.myTopicTotals}
                      medalion={gameboardTotals?.myTopicTotals.myMedalTopic}
                      renderCardFunction={renderCard}
                    />
                  </>
                )}
              </Container>
            )
          )}
        </div>

        <MainMenu inEventMode={isEventTeam} />
      </div>
    </DefaultLayout>
  );
}

export default GlobalGameBoard;
